import userLoggedIn from './scripts/user-logged-in'
import SiteMessagesInstance from './components/site-messages'
import getToken from './scripts/get-token'
import getSensors from './scripts/get-sensors'
import ImageCache from './util/class-image-cache'

import { StepStartNameSensor } from './templates/onboarding/step-start-name-sensor'

import { StepGuideSelectModel } from './templates/onboarding/step-guide-select-model'
import { StepGuideV1SetupMode } from './templates/onboarding/step-guide-v1-setup-mode'
import { StepGuideV1SetupHelp } from './templates/onboarding/step-guide-v1-setup-help'
import { StepGuideV2SetupMode } from './templates/onboarding/step-guide-v2-setup-mode'
import { StepGuideV2SetupHelp } from './templates/onboarding/step-guide-v2-setup-help'
import { StepStartApp } from './templates/onboarding/step-start-app'
import { StepStartBrowser } from './templates/onboarding/step-start-browser'
import { StepAppOnly } from './templates/onboarding/step-app-only'
import { StepConnectManually } from './templates/onboarding/step-connect-manually'
import { StepBeforeAttemptToConnect } from './templates/onboarding/step-before-attempt-to-connect'
import { StepAttemptToConnect } from './templates/onboarding/step-attempt-to-connect'
import { StepConnectedWifiSelect } from './templates/onboarding/step-connected-wifi-select'
import { StepConnectedTryWifi } from './templates/onboarding/step-connected-try-wifi'
import { StepConnectedTcSetup } from './templates/onboarding/step-connected-tc-setup'
import { StepAfterSetupReconnecting } from './templates/onboarding/step-after-setup-reconnecting'
import { StepAfterSetupNameSensor } from './templates/onboarding/step-after-setup-name-sensor'
import { StepConnectedDisconnect } from './templates/onboarding/step-connected-disconnect'
import { StepAfterSetupSelectiveQuestions } from './templates/onboarding/step-after-setup-selective-questions'
import Branding from './components/branding'
import RequestQueueInstance from './util/request-queue'
import isSelectiveSensor from './util/is-selective-sensor'
import isEnterpriseSubAccount from './util/is-enterprise-sub-account'
import isNistAccount from './util/is-nist-account'
import maybeBlacklistedOverlay from './components/maybe-blacklisted-overlay'
import {StepSetupWifiPassword} from "./templates/onboarding/step-setup-wifi-password";
import {StepSetupAlerts} from "./templates/onboarding/step-setup-alerts";
import {StepSetupContacts} from "./templates/onboarding/step-setup-contacts";
import {StepConnectedLocation} from "./templates/onboarding/step-connected-location";
import {StepConnectedZipCode} from "./templates/onboarding/step-connected-zip-code";
import {StepConnectedPipeQuestion} from "./templates/onboarding/step-connected-pipe-question";
import {StepBulkWifi} from "./templates/onboarding/step-bulk-wifi";
import {StepBulkRun} from "./templates/onboarding/step-bulk-run";
import { PIPE_STRAP_DECLINED } from './util/selective-pipe-strap'
import { webBluetoothBridge, webBluetoothDeviceAvailable, webBluetoothUnavailableReason } from './util/temp-stick-ble-web'
import { isBetaSite } from './util/beta-site'


class Onboarding {

  id = "setup";
  is_trying_wifi = false;
  sensor_version = 'v2';
  completed_setup = false;

  steps = {};

  ImageCache = null;

  // simulation mode
  simulation_enabled = false;

  constructor () {

    if(maybeBlacklistedOverlay()) {
      return;
    }

    this.container = document.querySelector('.content-container.onboarding');
    this.ImageCache = new ImageCache();

    if(!this.container) {
      return;
    }

    // set global flag to stop offline alerts
    window.is_onboarding = true;

    this.refreshRememberedWifiExpiry();

    userLoggedIn().then(async token => {

      if (token === false) {
        window.location.href = window.site.home + '/logout';
        return;
      }

      Branding.updateLogo();

      // Decides whether a plain browser gets the Bluetooth option; has to be
      // known before start() routes the first step.
      await this.detectWebBluetooth();

      // initializing each step
      [
        StepGuideSelectModel,
        StepGuideV1SetupMode,
        StepGuideV1SetupHelp,
        StepGuideV2SetupMode,
        StepGuideV2SetupHelp,
        StepStartNameSensor,
        StepSetupWifiPassword,
        StepSetupAlerts,
        StepSetupContacts,
        StepStartApp,
        StepStartBrowser,
        StepAppOnly,
        StepConnectManually,
        StepBeforeAttemptToConnect,
        StepAttemptToConnect,
        StepConnectedLocation,
        StepConnectedZipCode,
        StepConnectedPipeQuestion,
        StepConnectedWifiSelect,
        StepConnectedTryWifi,
        StepConnectedTcSetup,
        StepAfterSetupReconnecting,
        StepAfterSetupSelectiveQuestions,
        StepAfterSetupNameSensor,
        StepConnectedDisconnect,
        StepBulkWifi,
        StepBulkRun,
      ].forEach(step => {

        const Step = new step(this);
        this.steps[Step.id] = Step;

      });

      this.displayOnboarding();

      this.pages = this.container.querySelectorAll('.onboarding-step');

      this.$title = $(this.container).find('.onboarding-heading__title');
      this.$backBtn = $(this.container).find('button[data-for="onboarding-back"]');
      this.$closeBtn = $(this.container).find('button[data-for="onboarding-close"]');

      // Everything warmed here is for the connected flow, which runs with the
      // phone offline. A site that can't run it has nothing to prepare for.
      if (this.canOnboard()) {
        this.cacheImages();
        this.prefetchExistingSensorNames();
        this.prefetchGeoLocation();
      }

      this.bindActions();
      this.start();

    });

  }

  // simulation helpers
  is_simulation() {
    if (this.simulation_enabled === true) {
      return true;
    } else {
      return false;
    }
  }

  sensorName = "";
  simulate_selective = false;

  // Testing-only: whether the simulated sensor reports its probe as plugged in.
  // Toggled from the probe step's "testing" control so the selective probe
  // template can be validated in both the connected and disconnected states.
  simulate_probe_plugged_in = false;

  // Whether the current sensor is part of the selective (fire-sprinkler)
  // program. Selective sensors always ship with a probe and use the dedicated
  // 2-option probe onboarding.
  isSelective() {
    if (this.simulate_selective) {
      return true;
    }

    const sid = (this.sensor && this.sensor.sid) ? this.sensor.sid : null;
    return isSelectiveSensor(sid);
  }

  simulateSelective() {
    this.simulation_enabled = true;
    this.simulate_selective = true;
  }

  // Whether the sensor being set up is enterprise related — which every sensor
  // on an enterprise-managed account is, since the enterprise pushes its alerts
  // down to all of them. These accounts get the alerts review and the contact
  // confirmation after setup: the alerts watching the sensor aren't theirs, and
  // an alert with nobody attached tells no one. A plain consumer account sets
  // its own alerts up afterwards and goes straight to the finish step.
  isEnterpriseSensor() {

    if (this.simulate_selective) {
      return true;
    }

    // A selective sensor is enterprise-managed by construction: the API links
    // the account to the selective enterprise the moment the sensor registers.
    // That happens during the Wi-Fi test, after window.currentUser was loaded,
    // so on a fresh account the sensor-ID rule is the one that is true in time.
    if (this.isSelective()) {
      return true;
    }

    return isEnterpriseSubAccount();

  }

  // The fire-sprinkler questions — where the pipe strap goes, and what kind of
  // sprinkler system it is on — are about the pipe sensor being set up, so they
  // follow the sensor (by ID) rather than the account: a Selective account
  // adding an ordinary ambient sensor is never asked either. The sensor-ID rule
  // is also the only one that is true in time, since the pipe question runs
  // while the phone is on the sensor's setup network, before the sensor has
  // registered and linked the account.
  showsSelectiveQuestions() {
    return this.isSelective();
  }

  // Some accounts don't name sensors during setup — the picker only gets in
  // their way and errors out — so the sensor ID stands in as the name.
  skipsNameStep() {
    return isNistAccount();
  }

  // ── NIST batch setup ───────────────────────────────────────────────────────
  // The calibration lab onboards dozens of sensors at a time and each one is
  // nothing but "name = ID, join the calibration Wi-Fi", so that account gets
  // a batch run over Bluetooth (util/nist-bulk-onboarding.js) instead of the
  // per-sensor screens. Nobody else sees it — a customer could do real damage
  // with it.
  bulk = null;             // the NistBulkOnboarding in progress
  bulk_active = false;     // set by the runner while it holds the Bluetooth link
  bulk_devices = [];       // the sensors picked off the nearby list
  bulk_wifi = null;        // { ssid, password } for the whole batch

  canBulkOnboard() {
    return isNistAccount();
  }

  startBulk(devices) {
    this.bulk = null;
    this.bulk_devices = (devices || []).slice();
    this.bulk_wifi = null;
    this.goToStep('bulk_wifi');
  }

  // Where the selective pipe sensor is being attached, answered on the
  // connected pipe-question step. It has to live here: the phone is offline
  // during the connected flow, so the after-setup questionnaire step submits
  // it once connectivity is back, and the naming step reads it to pre-select
  // the matching name.
  pipe_strap_location = '';

  // Which probe was picked on the probe step, as the option shown there (CLAMP,
  // VELCRO_STRAP, WINE_BOTTLE, BUFFER_BOTTLE, or a bare thermocouple type) —
  // not the type it persists as, which folds the clamp and strap together. The
  // alerts review names the probe alert after it.
  probe_selection = '';

  // The zip code answered on the connected zip-code step (enterprise-managed
  // accounts only) and whether it was typed or an accepted suggestion. Held
  // here like the pipe answer: the phone is offline on that step, so the
  // reconnect step submits it once the sensor is registered and the phone is
  // back online.
  sensor_zip_code = '';
  sensor_zip_code_source = '';
  sensor_zip_code_submitted = false;

  // The zip code to suggest on that step, fetched up front while the phone
  // still has internet: the account's saved answer from an earlier setup, or —
  // the one time per account the API asks its geolocation provider — where the
  // request IP places the phone. See prefetchGeoLocation().
  geo_location = null;

  // ── Connected-flow routing ─────────────────────────────────────────────────
  // Once the sensor is identified the flow runs:
  //   connected_location → connected_zip_code (enterprise-managed sensors)
  //   → connected_pipe_question (pipe sensors only, by sensor ID)
  //   → connected_tc_setup (sensors with a probe) → naming → wifi select.
  // Forward and back both read the same conditions so back always retraces
  // the path forward actually took.

  // The zip code feeds the power-outage and weather alerts an enterprise runs
  // against its sensors' locations, so it's asked wherever those apply.
  showsZipCodeStep() {
    return this.isEnterpriseSensor();
  }

  goToStepAfterLocation() {

    if (this.showsZipCodeStep()) {
      this.goToStep('connected_zip_code');
      return;
    }

    this.goToStepAfterZipCode();

  }

  goToStepAfterZipCode() {

    if (this.showsSelectiveQuestions()) {
      this.goToStep('connected_pipe_question');
      return;
    }

    this.goToStepAfterPipeQuestion();

  }

  goToStepAfterPipeQuestion() {

    if (this.showsProbeStep()) {
      this.goToStep('connected_tc_setup');
      return;
    }

    this.goToNameStep();

  }

  showsProbeStep() {

    if (!this.sensor || this.sensor.hasTc !== true) {
      return false;
    }

    // "Not using the pipe sensor": the selective probe step only offers the
    // two pipe probes and won't continue without one selected, so there'd be
    // nothing for that user to pick. Generic probe sensors keep their step —
    // it has its own "I'm not using a probe" escape.
    if (this.isSelective() && this.pipe_strap_location === PIPE_STRAP_DECLINED) {
      return false;
    }

    return true;

  }

  stepBeforeNameStep() {

    if (this.showsProbeStep()) {
      return 'connected_tc_setup';
    }

    return this.stepBeforeProbeStep();

  }

  stepBeforeProbeStep() {
    return this.showsSelectiveQuestions() ? 'connected_pipe_question' : this.stepBeforePipeQuestion();
  }

  stepBeforePipeQuestion() {
    return this.showsZipCodeStep() ? 'connected_zip_code' : 'connected_location';
  }

  // Every route into the naming step goes through here so the skip lives in one
  // place. When it's skipped the sensor ID becomes the name and we go straight
  // on to placement/wifi; the completion step still shows it and can change it.
  goToNameStep() {

    if (!this.skipsNameStep()) {
      this.goToStep('start_name');
      return;
    }

    if (!this.sensorName && this.sensor && this.sensor.sid) {
      this.sensorName = this.sensor.sid;
    }

    this.goToStep('connected_wifi_select');

  }

  // The step "back" from wifi select returns to. With naming skipped, back
  // lands on whatever preceded the naming step for this sensor.
  stepBeforeWifiSelect() {

    if (!this.skipsNameStep()) {
      return 'start_name';
    }

    return this.stepBeforeNameStep();

  }

  enableSimulation() {
    this.simulation_enabled = true;
  }

  simulateConnection() {

    this.manual_connect = false;
    this.is_trying_wifi = true;

    this.goToStep("before_attempt_to_connect");

    setTimeout(() => {
      this.goToStep("attempt_to_connect");
    }, 500);

  }

  sensor = null;
  current_step = null;

  // Step to land on when the connected-disconnect prompt is cancelled — the
  // step that was active when it opened, so cancelling can't skip earlier
  // connected steps (location, pipe question, probe, naming).
  disconnect_return_step = null;

  goToDisconnectStep() {

    if (this.current_step === 'connected_disconnect') {
      return;
    }

    this.disconnect_return_step = this.current_step;
    this.goToStep('connected_disconnect');

  }

  // Whether this site can run the setup flow at all. Pairing needs a way to
  // reach the sensor: the app's native bridge joins its "Sensor Setup"
  // network, and a browser allowed to use Web Bluetooth can talk to it
  // directly. A browser-only deployment (mytempstick.com) says through
  // includes/site.php that it has no Wi-Fi path, and without Bluetooth either
  // it gets the app-only notice instead of a flow that dead-ends at the QR
  // scanner.
  canOnboard() {
    return window.site.canOnboard !== false || this.web_ble_enabled;
  }

  start() {

    if (!this.canOnboard()) {
      this.goToStep('app_only');
      return;
    }

    const quickAdd = this.consumeQuickAdd();

    if (quickAdd) {
      if (quickAdd.sensor_version) {
        this.sensor_version = quickAdd.sensor_version;
      }

      this.goToConnection();
      return;
    }

    this.goToStep("guide_select_model");
    // this.goToStep("step_setup_contacts");

  }

  cacheImages() {

    const base = `${window.site.home}/../assets/images`;

    // Images swapped in via JS at runtime, so they aren't in the DOM yet when
    // we scan below. Keep this list in sync whenever a step sets an <img> src
    // dynamically (e.g. .attr('src', ...) or markup injected on activate).
    const dynamicImages = [
      `${base}/setup-hero-location.png`,            // step-connected-location (default hero)
      `${base}/setup-hero-selective-location.png`,  // step-connected-location (selective hero)
      `${base}/setup/probe-velcro-strap.png`,       // step-connected-tc-setup (selective probe, injected on activate)
    ];

    // Every <img> baked into a step template is already in the DOM at this
    // point — displayOnboarding() renders all steps up front (just hidden) —
    // so collect them automatically. This stays correct as templates change,
    // instead of drifting from a hand-maintained list. Empty srcs (heroes that
    // get their real src later) are dropped and covered by dynamicImages above.
    const templateImages = Array.from(
      this.container.querySelectorAll('img[src]'),
      img => img.getAttribute('src')
    );

    // De-dupe and warm the browser cache now, while we still have internet —
    // once the phone joins the sensor's "Sensor Setup" network there is no
    // internet, so any image not already cached would fail to load.
    const urls = Array.from(new Set([...templateImages, ...dynamicImages]))
      .filter(Boolean);

    this.ImageCache.cacheImages(urls);

  }

  // Names already in use on this account. The naming step runs while the phone
  // is joined to the sensor's "Sensor Setup" network — no internet — so grab
  // them now, up front, the same way we warm the image cache.
  existing_sensor_names = [];

  prefetchExistingSensorNames() {

    // Only the naming step reads these, so don't pay for the call when it's
    // skipped — these accounts tend to be the ones with the most sensors.
    if (this.skipsNameStep()) {
      return;
    }

    getSensors().then(sensors => {

      this.existing_sensor_names = (sensors || [])
        .map(sensor => (sensor && sensor.sensor_name) ? String(sensor.sensor_name).trim() : '')
        .filter(name => name.length > 0);

    }).catch(() => {
      // Non-fatal: the naming step just falls back to the curated list alone.
    });

  }

  // The zip code to suggest on the connected zip-code step. Fetched now, while
  // the phone still has internet, for the same reason as the images and names
  // above: by the time the step runs the phone is on the sensor's network and
  // nothing can be fetched. The API answers from the account's saved zip code
  // when it has one and only ever asks its geolocation provider once per
  // account, so this is cheap to call on every setup and harmless for accounts
  // that won't see the step.
  //
  // One request at a time: a second call while the first is in flight would
  // race it, and on a brand-new account two concurrent calls could each
  // trigger the provider lookup.
  geo_location_request = null;
  geo_location_failed = false;

  prefetchGeoLocation() {

    if (this.geo_location_request) {
      return this.geo_location_request;
    }

    this.geo_location_failed = false;

    const settle = () => {
      this.geo_location_request = null;
    };

    this.geo_location_request = window.apiRequest('get', '/user/geo-location').then(r => {

      if (r && r.type === 'success' && r.data && r.data.postal) {
        this.geo_location = r.data;
      }

      settle();

    }).catch(() => {
      // Non-fatal here: the field would simply start empty. Remembered so the
      // last online moment can try once more (ensureGeoLocation).
      this.geo_location_failed = true;
      settle();
    });

    return this.geo_location_request;

  }

  // The connection step is the last moment the phone is on the internet. A
  // lookup that could not get through at page start gets one more try here;
  // one that answered — with or without a suggestion — is not repeated.
  ensureGeoLocation() {

    if (this.geo_location || this.geo_location_request || !this.geo_location_failed) {
      return;
    }

    this.prefetchGeoLocation();

  }

  // Save the zip code answered on the connected zip-code step. Called from the
  // reconnect step once the sensor is registered and the phone is online. A
  // request that can't get through is left on the offline queue to retry, so
  // an answer given is never lost; an answer the API refuses won't get better
  // on retry and is logged instead.
  async submitSensorZipCode() {

    const zip = String(this.sensor_zip_code || '').trim();

    if (!zip || this.sensor_zip_code_submitted || !this.sensor || !this.sensor.sid) {
      return;
    }

    this.sensor_zip_code_submitted = true;

    // Don't pollute the real request queue while simulating a test run.
    if (this.is_simulation()) {
      return;
    }

    const endpoint = `/sensor/${encodeURIComponent(this.sensor.sid)}/location`;
    const data = {
      zip_code: zip,
      zip_code_source: this.sensor_zip_code_source || 'user',
    };

    try {

      const r = await window.apiRequest('post', endpoint, data);

      if (!r || r.type !== 'success') {
        console.error('Sensor zip code not saved', r);
      }

    } catch (e) {
      console.error(e);
      RequestQueueInstance.add(endpoint, data);
    }

  }

  goToConnection() {

    // last chance to fetch anything before the phone joins the sensor's network
    this.ensureGeoLocation();

    // app mode: the native bridge can reach the sensor over Wi-Fi or Bluetooth
    // (Bluetooth only counts on beta / localhost, see bleBridge())
    if(
      (
        window.gonative &&
        window.gonative.nativebridge &&
        (window.gonative.nativebridge.wifiConnect || (window.gonative.nativebridge.ble && isBetaSite()))
      ) ||
      (window.site.home.includes("temp-stick-dashboard.localhost"))
    ) {

      // only work if the app is up to date otherwise use the web browser dumb mode

      if(this.sensor_version === 'v1') {

        // go to manual page for v1 sensor
        this.goToStep('connect_manually');

      } else {
        this.goToStep('start_app');
      }

    } else {
      // desktop mode
      this.goToStep('start_browser');
    }

  }

  hideBackButton() {
    this.$backBtn.removeClass('is-active');
  }

  showBackButton() {
    this.$backBtn.toggleClass('is-active', true);
  }

  goToStep(step) {

    // Let the step being left tidy up (stop a Bluetooth scan, clear timers).
    const leaving = this.current_step;

    if (leaving && leaving !== step && this.steps[leaving] && typeof this.steps[leaving].onDeactivate === 'function') {
      try {
        this.steps[leaving].onDeactivate();
      } catch (e) {
        console.error(e);
      }
    }

    this.current_step = step;

    $(this.pages).removeClass('is-active');
    this.$current_step = $(this.container).find(`.onboarding-step[data-for="${this.current_step}"]`);

    this.$title.html(this.steps[this.current_step].title);

    this.$current_step.addClass('is-active');

    if(this.interval_get_wifi_networks) {
      clearInterval(this.interval_get_wifi_networks);
    }

    if(this.steps[step].onActivate) {

      console.log(step);
      console.log(this.steps[step]);
      this.steps[step].onActivate();

    }

  }

  displayOnboarding() {

    $(this.container).html(`
      
      <div class="onboarding-heading">
        <div class="onboarding-heading__back">
          <button type="button" class="button" data-for="onboarding-back">
            <i class="far fa-chevron-left"></i>  
          </button>
        </div>
        <div class="onboarding-heading__title">Set up a Temp Stick</div>
      </div>
          
      <div class="onboarding-content">
      
        ${Object.keys(this.steps).map(step_id => {
          return this.steps[step_id].template;  
        }).join('')}
        
        <div class="onboarding-about-sensor"></div>
        
      </div>
      
      <div class="onboarding-assistance">
        Need assistance? Call <b>888.263.6973</b>
      </div>
              
      `);

  }

  async Update_Connected_Sensor() {

    const $aboutSensor = $(this.container).find('.onboarding-about-sensor');

    const sensor = await this.Temp_Stick_Request("GET", "/sid");

    this.sensor = sensor;

    if(this.sensor && this.sensor.sid) {
      Branding.maybeUpdateBranding(this.sensor.sid)
    }

    // display additional information about the sensor
    $aboutSensor.addClass('is-active');
    $aboutSensor.html(`
            <div class="onboarding-about-sensor__id">ID: ${this.sensor.sid}</div>
            <div class="onboarding-about-sensor__mac">MAC: ${this.sensor.mac}</div>
            <div class="onboarding-about-sensor__transport">via ${this.isBle() ? 'Bluetooth' : 'Wi-Fi'}</div>
          `);

    return sensor;

  }

  Set_Connected(is_connected) {
    this.is_connected = is_connected;

    if(this.is_connected) {
      $('body').addClass('is-connected-to-sensor');
    } else {
      $('body').removeClass('is-connected-to-sensor');
    }

  }

  // whether we're using the QR code or if they manually connected
  manual_connect = false;

  // ── Sensor transport ───────────────────────────────────────────────────────
  // How the phone reaches the sensor while it's in setup mode:
  //   'wifi' — the phone joins the sensor's "Sensor Setup" access point and the
  //            native bridge speaks HTTP to 10.10.1.1 (the original path)
  //   'ble'  — a Bluetooth LE link carrying the very same requests and
  //            responses (TempStickBle plugin in the app; wire format in
  //            temp-stick-app-ionic/firmware/esp32s3-ble-sim/PROTOCOL.md)
  // Every step talks through Temp_Stick_Request(), so none of them know or
  // care which one carried the call. Bluetooth is tried first when the app
  // offers it — the phone keeps its own internet, and there's no "join this
  // network with no internet" prompt to get wrong — and Wi-Fi remains the
  // fallback for sensors that don't advertise.
  transport = 'wifi';

  // Which link the connection attempt is on right now, so the interstitial can
  // say "looking over Bluetooth" rather than "when prompted, join Sensor Setup".
  connect_phase = 'wifi';

  // How long to look for the sensor over Bluetooth before falling back. A
  // sensor that advertises is found well inside this; a Wi-Fi-only sensor
  // costs the full wait once, before the Wi-Fi prompt appears.
  ble_connect_timeout_ms = 7000;

  // 'auto' tries Bluetooth first and falls back to Wi-Fi; 'ble' or 'wifi'
  // force one. For testing: ?transport=ble|wifi|auto, or set the localStorage
  // key below to the same values.
  transport_storage_key = 'tempstick_onboarding_transport';

  transportPreference() {

    const valid = ['auto', 'ble', 'wifi'];

    try {
      const fromQuery = new URLSearchParams(window.location.search || '').get('transport');
      if (fromQuery && valid.includes(fromQuery)) {
        return fromQuery;
      }
    } catch (e) {
      // ignore
    }

    try {
      const fromStorage = window.localStorage ? window.localStorage.getItem(this.transport_storage_key) : null;
      if (fromStorage && valid.includes(fromStorage)) {
        return fromStorage;
      }
    } catch (e) {
      // ignore
    }

    return 'auto';

  }

  // Whether this page may use the browser's own Bluetooth for setup. Beta only
  // while it proves itself, and only where it can actually work: the browser
  // has Web Bluetooth (Chrome/Edge; not Safari or Firefox) and the device
  // reports a usable adapter. Everywhere else the browser keeps today's
  // behaviour — the Wi-Fi instructions or the use-the-app notice.
  web_ble_enabled = false;

  // When it's off on beta, why (see webBluetoothUnavailableReason), so the
  // connection step can tell a tester what to change instead of just not
  // offering it. Null when enabled or not on beta.
  web_ble_reason = null;

  async detectWebBluetooth() {

    this.web_ble_enabled = false;
    this.web_ble_reason = null;

    if (!isBetaSite()) {
      return;
    }

    try {
      this.web_ble_enabled = await webBluetoothDeviceAvailable();
      if (!this.web_ble_enabled) {
        this.web_ble_reason = await webBluetoothUnavailableReason();
      }
    } catch (e) {
      this.web_ble_enabled = false;
    }

  }

  // A one-line explanation for beta testers when a browser gets no Bluetooth
  // option; empty when there's nothing to explain (enabled, in the app, or
  // not on beta).
  webBleUnavailableNote() {

    if (this.web_ble_enabled || !isBetaSite() || window.site.isApp) {
      return '';
    }

    switch (this.web_ble_reason) {
      case 'brave':
        return 'Brave turns Web Bluetooth off by default. Enable it at <b>brave://flags/#brave-web-bluetooth-api</b>, relaunch Brave and reload this page to set up over Bluetooth.';
      case 'no-adapter':
        return 'No Bluetooth adapter is available on this computer, so setup over Bluetooth is not offered here.';
      case 'no-api':
        return 'Setting up over Bluetooth from a computer works in Chrome or Edge; this browser does not support it.';
      default:
        return '';
    }

  }

  // The Bluetooth bridge: the app's native one when this build has it, else
  // the browser's own Web Bluetooth when detectWebBluetooth() allowed it,
  // else null. Both speak the same calls, so the rest of the flow doesn't
  // care which. The web one has `chooser: true` — the browser picks the
  // sensor from a dialog instead of a scan we can list.
  //
  // Bluetooth setup as a whole is only offered on beta.mytempstick.com and
  // localhost while it proves itself. On temperaturestick.com this is always
  // null, so every step behaves as if the app had no Bluetooth: the QR-first
  // screen, the Sensor Setup Wi-Fi network, and no nearby list, Bluetooth
  // buttons or bulk run anywhere.
  bleBridge() {

    if (!isBetaSite()) {
      return null;
    }

    const native = window.gonative && window.gonative.nativebridge && window.gonative.nativebridge.ble;

    if (native && typeof native.connect === 'function') {
      return native;
    }

    if (this.web_ble_enabled) {
      return webBluetoothBridge();
    }

    return null;

  }

  isBle() {
    return this.transport === 'ble';
  }

  // Where a connection attempt starts, given the preference and what the app
  // can do.
  initialConnectPhase() {
    return (this.transportPreference() !== 'wifi' && this.bleBridge()) ? 'ble' : 'wifi';
  }

  setConnectPhase(phase) {

    this.connect_phase = phase;

    const step = this.steps['before_attempt_to_connect'];

    if (step && typeof step.render === 'function') {
      step.render(phase);
    }

  }

  async Temp_Stick_Auto_Connect() {

    const preference = this.transportPreference();

    if (preference !== 'wifi' && this.bleBridge()) {

      this.setConnectPhase('ble');

      const res = await this.Temp_Stick_Connect_Ble({ sensorId: this.sensorId });

      if (res.success) {
        return;
      }

      console.log('Bluetooth connect failed, falling back to Wi-Fi', res);

      if (preference === 'ble') {
        throw new Error(res.message || 'Bluetooth connect failed');
      }

    }

    this.setConnectPhase('wifi');
    this.transport = 'wifi';

    await this.Temp_Stick_Connect_Wifi();

  }

  // Join the sensor's "Sensor Setup <id>" access point through the app.
  Temp_Stick_Connect_Wifi() {

    return new Promise((resolve, reject) => {

      if (!(window.gonative && window.gonative.nativebridge && window.gonative.nativebridge.wifiConnect)) {
        reject(new Error('Wi-Fi bridge not available'));
        return;
      }

      window.gonative.nativebridge.wifiConnect({
        ssid: "Sensor Setup " + this.sensorId,
        password: "",
        callback: res => {

          // scream internally
          if (res.success == "true" || res.success == true) {
            resolve();
          } else {
            reject();
          }

        }

      });

    });

  }

  // Connect over Bluetooth to the sensor with this id, or to a device picked
  // from a scan (address). Resolves with the bridge's result either way; on
  // success the transport switches to 'ble'.
  Temp_Stick_Connect_Ble({ sensorId = '', address = '', timeoutMs = 0 } = {}) {

    return new Promise(resolve => {

      const ble = this.bleBridge();

      if (!ble) {
        resolve({ success: false, code: 'UNAVAILABLE', message: 'Bluetooth bridge not available' });
        return;
      }

      ble.connect({
        sensorId,
        address,
        timeoutMs: timeoutMs || this.ble_connect_timeout_ms,
        callback: res => {

          res = res || {};

          if (res.success == true || res.success == "true") {

            this.transport = 'ble';
            this.ble_device = res;
            this.watchBleDisconnect();

            resolve(Object.assign({}, res, { success: true }));

          } else {
            resolve(Object.assign({}, res, { success: false }));
          }

        }
      });

    });

  }

  // Live discovery for the start step (app bridge only): the bridge keeps
  // scanning and reports every sighting as a 'tempstick:ble-device' DOM
  // event { name, address, rssi, sensorId }, then 'tempstick:ble-scan-stopped'
  // { reason } when it ends. The browser bridge can't do this — its connect()
  // is the chooser — and says so with code CHOOSER.
  Temp_Stick_Ble_Start_Scan({ timeoutMs = 120000, sensorId = '' } = {}) {

    return new Promise(resolve => {

      const ble = this.bleBridge();

      if (!ble || typeof ble.startScan !== 'function') {
        resolve({ success: false, code: 'UNAVAILABLE' });
        return;
      }

      if (ble.chooser) {
        resolve({ success: false, code: 'CHOOSER' });
        return;
      }

      ble.startScan({
        timeoutMs,
        sensorId,
        callback: res => resolve(res || { success: false, code: 'UNAVAILABLE' })
      });

    });

  }

  Temp_Stick_Ble_Stop_Scan() {

    return new Promise(resolve => {

      const ble = this.bleBridge();

      if (!ble || typeof ble.stopScan !== 'function') {
        resolve({ success: true, wasScanning: false });
        return;
      }

      ble.stopScan({ callback: res => resolve(res || { success: true }) });

    });

  }

  // What the bridge can do right now — { available, enabled, permitted,
  // authorization?, connected } — without triggering any permission prompt.
  // Both apps promise isAvailable() never asks; scan()/connect()/enable() do,
  // so the start step calls this first and only scans once the reason is on
  // screen (see StepStartApp.beginNearbyDiscovery).
  Temp_Stick_Ble_Availability() {

    return new Promise(resolve => {

      const ble = this.bleBridge();
      const none = { available: false, enabled: false, permitted: false };

      if (!ble || typeof ble.isAvailable !== 'function') {
        resolve(none);
        return;
      }

      ble.isAvailable({ callback: res => resolve(res || none) });

    });

  }

  // Nearby Temp Sticks in setup mode, for the manual path where there's no QR
  // code to say which one.
  Temp_Stick_Scan_Ble(timeoutMs = 7000) {

    return new Promise(resolve => {

      const ble = this.bleBridge();

      if (!ble || typeof ble.scan !== 'function') {
        resolve({ success: false, code: 'UNAVAILABLE', devices: [] });
        return;
      }

      ble.scan({
        timeoutMs,
        callback: res => resolve(res || { success: false, devices: [] })
      });

    });

  }

  // Drop the link to the sensor. Wi-Fi: release the "Sensor Setup" network so
  // the phone returns to its own; Bluetooth: close the connection. Callers
  // delay this so a fire-and-forget /restart or /done gets out first.
  Temp_Stick_Disconnect() {

    if (this.isBle()) {

      const ble = this.bleBridge();

      if (ble && typeof ble.disconnect === 'function') {
        ble.disconnect({ callback: () => {} });
      }

      return;

    }

    if (window.gonative && window.gonative.nativebridge && window.gonative.nativebridge.wifiDisconnect) {
      window.gonative.nativebridge.wifiDisconnect();
    } else {
      console.log("window.gonative.nativebridge.wifiDisconnect() is not defined");
    }

  }

  ble_disconnect_bound = false;

  watchBleDisconnect() {

    if (this.ble_disconnect_bound) {
      return;
    }

    this.ble_disconnect_bound = true;

    window.addEventListener('tempstick:ble-disconnected', e => {
      this.onBleDisconnected((e && e.detail) || {});
    });

  }

  // The Bluetooth link dropped underneath us (sensor restarted, went to sleep,
  // out of range). The heartbeat would notice within a minute; this is the
  // same outcome, straight away.
  onBleDisconnected(detail) {

    if (!this.isBle()) {
      return;
    }

    console.log('Bluetooth link to the sensor closed', detail);

    // Expected once setup is done, while a disconnect step is closing it, or
    // during a batch run, which hangs up on every sensor itself.
    if (
      this.bulk_active ||
      this.completed_setup ||
      !this.is_connected ||
      this.current_step === 'connected_disconnect' ||
      this.current_step === 'after_setup_reconnecting'
    ) {
      return;
    }

    this.Set_Connected(false);
    this.attempt_count = 0;

    SiteMessagesInstance.addMessage(
      "error",
      "We lost the Bluetooth connection to the Temp Stick, make sure it's still in setup mode and try again"
    );
    SiteMessagesInstance.displayMessages();

    this.goToStep('start_app');

  }

  Temp_Stick_Request_Simulate(type, path, data = {}, skipResponse = false) {

    if (skipResponse) {
      return Promise.resolve();
    }

    return new Promise(resolve => {

      setTimeout(() => {

        switch (path) {

          case '/wirelessNetworks': {

            let _simWifiList = [
              { ssid: "SimNet1", password: 0, id: 1 },
              { ssid: "SimNet2P", password: 1, id: 2 },
              { ssid: "SimNet3", password: 0, id: 3 },
              { ssid: "SimNet4P", password: 1, id: 4 },
              { ssid: "SimNet5", password: 0, id: 5 },
              { ssid: "SimNet6P", password: 1, id: 6 },
              { ssid: "SimNet7", password: 0, id: 7 },
              { ssid: "SimNet8P", password: 1, id: 8 },
              { ssid: "SimNet9", password: 0, id: 9 },
              { ssid: "SimNet10P", password: 1, id: 10 }
            ];

            const copy = _simWifiList.slice();

            const selected = [];

            for (let i = 0; i < 3; i++) {

              const index = Math.floor(Math.random() * copy.length);

              selected.push(copy[index]);

              copy.splice(index, 1);
            }

            resolve(selected);

            break;
          }

          case '/wlan': {
            resolve();
            break;
          }

          case '/status': {
            let status = 99;
            if (this.current_step === 'connected_try_wifi') {
              status = 9;
            }
            resolve(status);
            break;
          }

          case '/sid': {
            resolve({
              sid: 'TS300SIMULATE',
              mac: '00:11:22:33:44:55'
            });
            break;
          }

          case '/get/readings': {

            const reading = {
              tempC: '20',
              humidity: '69'
            };

            // On the selective test path the probe step reads tcTempC to show a
            // live probe status. Reflect the "probe plugged in" test toggle: a
            // sub -200 sentinel means disconnected (mirrors the firmware).
            if (this.simulate_selective) {
              reading.tcTempC = this.simulate_probe_plugged_in ? 21.5 : -400;
            }

            resolve(reading);
            break;
          }

          case '/acc': {
            resolve({
              t: 'ok',
              msg: 'registered'
            });
            break;
          }

          case '/restart': {
            resolve({
              t: 'ok'
            });
            break;
          }

          default: {
            resolve({
              t: 'ok'
            });
            break;
          }

        }

      }, 250);

    });

  }

  Temp_Stick_Request(type, path, data={}, skipResponse=false) {

    if (this.is_simulation()) {
      return this.Temp_Stick_Request_Simulate(type, path, data, skipResponse);
    }

    return new Promise((resolve, reject) => {

      // Both native transports answer with the same { success, response }
      // shape, so one handler serves the Wi-Fi and Bluetooth bridges.
      const onBridgeResult = res => {

        try {
          if (res && (res.success == true || res.success == "true")) {

            let data = String(res.response == null ? '' : res.response).trim();

            if (data[0] === '{' || data[0] === '[') {

              try {
                data = JSON.parse(data);
              } catch (e) {
                data = String(res.response).trim();
              }

            }

            if(!skipResponse) {
              resolve(data);
            }

          } else {
            if(!skipResponse) {
              reject();
            }
          }

        } catch (e) {

          if(!skipResponse) {
            reject();
          }
        }

      };

      if (this.isBle()) {

        const ble = this.bleBridge();

        if (!ble || typeof ble.request !== 'function') {
          if(!skipResponse) {
            reject();
          } else {
            resolve();
          }
          return;
        }

        ble.request({
          path,
          type,
          data,
          skipResponse,
          callback: onBridgeResult
        });

        // resolve if we skip
        if(skipResponse) {
          resolve();
        }

        return;

      }

      if(window.site.home.includes("temp-stick-dashboard.localhost")) {

        $.ajax({
          url: `http://10.10.1.1${path}`,
          type,
          data,
          success: res => {

            try {
              resolve(res);

            } catch (e) {
              reject();
            }

          },
          error: () => {
            reject();
          }
        });

      } else {

        window.gonative.nativebridge.TempStickRequest({
          path,
          type,
          data,
          skipResponse,
          callback: onBridgeResult
        });

        // resolve if we skip
        if(skipResponse) {
          resolve();
        }
      }

    });

  }

  current_status = 99;

  onTempStickHeartbeat(status) {

    status = parseInt(status);

    // 0 = failed to connect to wifi
    // 1 = no internet
    // 2 = server unavailable
    // 9 = ready to complete
    // 99 = start

    this.current_status = status;

    if(
      (
        status === 99 ||
        status === 0 ||
        status === 1 ||
        status === 2 ||
        status === 9
      ) && (
        this.current_step === 'attempt_to_connect' ||
        this.current_step === 'connect_manually' ||
        this.current_step === 'start_app'
      )
    ) {

      this.Update_Connected_Sensor().then(sensor => {

        // EX sensors have a probe. Selective sensors are EX, so they're
        // flagged here too; the simulated selective test path has no EX sid,
        // so flag it explicitly to expose the probe template for validation.
        if(sensor.sid.substr(0, 2) === 'EX' || this.simulate_selective) {
          this.sensor.hasTc = true;
        }

        // The connected flow opens with the take-it-to-its-spot interstitial —
        // the pipe question, probe setup, naming, and the wifi coverage check
        // all assume the user is standing at the install location. Routing
        // happens after the scan so we already know what kind of sensor this
        // is and each step can tailor itself to it.
        this.goToStep('connected_location');

      });

    } else if(this.current_step === 'connected_try_wifi') {

      // ready to register the sensor
      if(status == 9) {
        this.Temp_Stick_Register_Sensor();
      } else if(status >= 70 && status < 80) {
        // do nothing for testing status states
        // 70 = test wifi
        // 71 = test internet
        // 72 = test server
      } else {
        switch(status) {
          case(0):
            SiteMessagesInstance.addMessage("error", "Failed to connect with those Wi-Fi credentials, please try again")
            break;
          case(1):
            SiteMessagesInstance.addMessage("error", "We were unable to connect to the internet, please check your firewall settings")
            break;
          case(2):
            SiteMessagesInstance.addMessage("error", "We had problems communicating to our server, please check your firewall settings")
            break;
          default:
            SiteMessagesInstance.addMessage("error", "Something went wrong during setup, please try again")
            break;
        }

        SiteMessagesInstance.displayMessages();
        this.goToStep('connected_wifi_select');

      }
    }
  }

  async Temp_Stick_Heartbeat() {

    // interval is already going
    if(this.status_interval) {
      return;
    }

    if(this.completed_setup) {
      return;
    }

    this.Set_Connected(false);
    this.attempt_count = 0;

    let is_getting_status = false;

    // this watches the status endpoint
    // if this starts failing we can assume we're not connected to the sensor
    this.status_interval = setInterval(async () => {
      try {

        // don't queue requests
        if(is_getting_status) {
          return;
        }

        if(this.completed_setup) {
          clearInterval(this.status_interval);
          return;
        }

        is_getting_status = true;

        // connected so we're no longer trying wifi
        this.is_trying_wifi = false;

        const data = await this.Temp_Stick_Request('GET', '/status');
        this.Set_Connected(true);
        this.attempt_count = 0;

        is_getting_status = false;
        this.onTempStickHeartbeat(data);

      } catch (e) {

        console.error(e);
        console.log(this.current_step, this.attempt_count);

        is_getting_status = false;
        this.attempt_count++;

        // attempt to connect check
        // fail after 30 seconds of attempting (either the Temp Stick isn't on or we don't have WiFi permissions)
        if(this.current_step === 'attempt_to_connect' && this.attempt_count > 3) {

          this.Set_Connected(false);
          this.attempt_count = 0;

          SiteMessagesInstance.addMessage(
            "error",
            "We couldn't connect to the Temp Stick, make sure it's in setup mode and check the app's WiFi permissions"
          );
          SiteMessagesInstance.displayMessages();

          this.goToStep('start_app');

          return;

        }

        if(this.attempt_count > 10) {

          let errorMessage = "Cannot connect to the Temp Stick. Ensure it's in setup mode and you're connected to the Sensor Setup network.";

          if(this.is_connected) {
            errorMessage = "We lost connection to the Temp Stick, you might not have a good signal or have been idle for too long";
            this.goToStep('start_app');
          }

          this.Set_Connected(false);
          this.attempt_count = 0;

          SiteMessagesInstance.addMessage(
            "error",
            errorMessage
          );
          SiteMessagesInstance.displayMessages();

        }

      }
    }, 5000);

  }

  remember_wifi_storage_key = 'tempstick_onboarding_wifi_v1';
  remember_wifi_ttl_ms = 60 * 60 * 1000;
  pending_remember_wifi = null;

  refreshRememberedWifiExpiry() {
    const key = this.remember_wifi_storage_key;
    const ttl = this.remember_wifi_ttl_ms;
    const now = Date.now();

    try {
      if (!window || !window.localStorage) {
        return;
      }

      const raw = window.localStorage.getItem(key);
      if (!raw) {
        return;
      }

      const data = JSON.parse(raw);
      if (!data || !data.ssid) {
        window.localStorage.removeItem(key);
        return;
      }

      const savedAt = (typeof data.saved_at === 'number') ? data.saved_at : 0;
      const expiresAt = (typeof data.expires_at === 'number') ? data.expires_at : 0;
      const computedExpiresAt = expiresAt || ((savedAt || now) + ttl);

      // Expired: delete it
      if (now >= computedExpiresAt) {
        window.localStorage.removeItem(key);
        return;
      }

      // Refresh timer on onboarding start
      window.localStorage.setItem(key, JSON.stringify({
        ssid: String(data.ssid),
        password: (typeof data.password === 'string') ? data.password : '',
        saved_at: now,
        expires_at: now + ttl
      }));

    } catch (e) {
      // ignore
    }
  }

  commitRememberedWifi() {
    if (!this.pending_remember_wifi) {
      return;
    }

    const now = Date.now();

    try {
      if (!window || !window.localStorage) {
        return;
      }

      window.localStorage.setItem(
        this.remember_wifi_storage_key,
        JSON.stringify({
          ssid: this.pending_remember_wifi.ssid,
          password: this.pending_remember_wifi.password || "",
          saved_at: now,
          expires_at: now + this.remember_wifi_ttl_ms
        })
      );

    } catch (e) {
      // ignore
    } finally {
      this.pending_remember_wifi = null;
    }
  }

  is_registering = false;

  async Temp_Stick_Register_Sensor() {

    if(this.is_registering) {
      return;
    }

    this.is_registering = true;
    let res = null;

    try {

      res = await this.Temp_Stick_Request("POST", "/acc", {
        t: "app",
        e: window.currentUser.email,
        p: getToken(),
        pa: getToken(),
      });

    } catch (e) {

      res = {
        err: 1,
        msg: "invalid_json"
      }

    }

    // validate the JSON and attempt to parse if it's a string
    if (typeof res === 'string') {

      res = res.trim();

      try {
        res = JSON.parse(res);
      } catch (e) {
        res = {
          err: 1,
          msg: "invalid_json"
        }
      }
    }

    // if we had an error processing /acc
    if(
      res.err == 1 ||
      res.err == "1" ||
      res.t == "err"
    ) {

      this.is_registering = false;

      if(res.msg && res.msg.includes('internet unavailable')) {
        SiteMessagesInstance.addMessage("error", "The sensor was unable to check into to our server, it might be blocked by a firewall")
      } else if(res.msg && res.msg === 'invalid_json') {
        SiteMessagesInstance.addMessage("error", "The sensor was unable to check into to our server, it might be blocked by a firewall");
      } else if(res.msg && res.msg.length > 0) {
        SiteMessagesInstance.addMessage("error", res.msg);
      }else {
        SiteMessagesInstance.addMessage("error", "There was a problem trying to set up the Temp Stick");
      }

      SiteMessagesInstance.displayMessages();

      this.goToStep('connected_wifi_select');

    } else {

      this.commitRememberedWifi();

      // get readings for sensor
      await this.GetReadings();

      // tell the sensor to restart
      this.Temp_Stick_Request("POST", "/restart", {}, true);

      // move into reconnect interstitial
      this.goToStep('after_setup_reconnecting');

    }

  }

  async GetReadings() {

    try {

      let readings = null;

      if(this.is_simulation()) {
        readings = await this.Temp_Stick_Request_Simulate('GET', '/get/readings');
      } else {
        readings = await this.Temp_Stick_Request('GET', '/get/readings');
      }

      this.last_reading = readings;

      console.log({readings});

    } catch (e) {
      console.error(e);
    }

  }

  async attemptToConnect() {

    // if we are using the app auto connect functionality
    if(!this.manual_connect) {

      this.is_trying_wifi = true;

      // show instructions while we prepare to connect manually
      this.setConnectPhase(this.initialConnectPhase());
      this.goToStep("before_attempt_to_connect");

      try {
        await this.Temp_Stick_Auto_Connect();
        this.goToStep("attempt_to_connect");
      } catch (e) {

        SiteMessagesInstance.addMessage(
          "error",
          "We couldn't automatically connect to your sensor, check the app's Bluetooth and Wi-Fi permission settings."
        );
        SiteMessagesInstance.displayMessages();

        this.goToStep('start_app')

      }

    }

  }

  consumeQuickAdd() {
    // Prefer query params (more reliable than storage in some mobile/webview edge cases)
    try {
      const params = new URLSearchParams(window.location.search || "");
      const qa = params.get("quick_add");
      const sv = params.get("sensor_version");

      if (qa === "1") {
        const out = {};
        if (sv) {
          out.sensor_version = sv;
        }
        return out;
      }
    } catch (e) {
      // ignore
    }

    // Fallback: existing localStorage behavior
    try {
      if (!window || !window.localStorage) {
        return null;
      }

      const raw = window.localStorage.getItem("tempstick_onboarding_quick_add");
      if (!raw) {
        return null;
      }

      window.localStorage.removeItem("tempstick_onboarding_quick_add");

      try {
        return JSON.parse(raw);
      } catch (e) {
        return {};
      }
    } catch (e) {
      return null;
    }
  }

  bindActions() {

    Object.keys(this.steps).forEach(step_id => {
      if(this.steps[step_id] && this.steps[step_id].bindActions) {
        this.steps[step_id].bindActions();
      }
    });

    this.$backBtn.on('click', () => {
      this.steps[this.current_step].onBack();
    });

    $('body').on('click', '.app-menu__link', e => {

      // do nothing if we're trying wifi
      if(this.is_trying_wifi) {
        e.preventDefault();
        return;
      }

      if(this.is_connected) {
        e.preventDefault();

        this.goToDisconnectStep();

      }
    });

    this.$closeBtn.on('click', () => {

      if(this.is_connected) {
        this.goToDisconnectStep();
      } else {
        window.location.href = window.site.home + "/sensors";
      }

    });

  }

}

// Kept on window so the testing-only simulate*() methods above can be called
// from the browser console against the live instance.
window.onboarding = new Onboarding();
